/**
 * ShortLinkRedirect — public page at /s/:slug
 *
 * Since all non-`/api` routes are served by the React frontend on this
 * platform (ingress rule), we can't do a server-side 302 for `/s/:slug` —
 * so we resolve the destination via the API and use `window.location.replace`
 * to redirect. The user sees a brief loading spinner (~150-300ms).
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import api from "@/lib/api";

export default function ShortLinkRedirect() {
  const { slug } = useParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/short-links/resolve/${encodeURIComponent(slug)}`);
        if (cancelled) return;
        if (data?.long_url) {
          window.location.replace(data.long_url);
        } else {
          setError("Not found");
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? "not_found" : "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafaf9] p-6" dir="rtl">
        <div className="bg-white rounded-3xl p-8 text-center max-w-sm border border-black/5 shadow-[0_12px_40px_rgba(28,25,23,0.08)]">
          <div className="w-14 h-14 rounded-2xl bg-rose-50 mx-auto flex items-center justify-center mb-4">
            <AlertTriangle className="w-7 h-7 text-rose-500" strokeWidth={2.5} />
          </div>
          <h2 className="text-xl font-[800] mb-2 text-brand-charcoal">הקישור לא נמצא</h2>
          <p className="text-stone-500 text-sm leading-relaxed mb-4">
            ייתכן שהקישור פג תוקף, בוטל או הודפס בשגיאה.
          </p>
          <Link
            to="/"
            data-testid="short-link-back-home"
            className="inline-flex items-center justify-center h-11 px-6 rounded-full bg-brand-amber text-white font-bold hover:bg-brand-amberHover transition"
          >
            חזרה לדף הבית
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fafaf9]" dir="rtl">
      <div className="text-center" data-testid="short-link-loading">
        <div className="w-12 h-12 rounded-full border-4 border-brand-amber border-t-transparent animate-spin mx-auto mb-3" />
        <p className="text-stone-500 text-sm">מעבירים אתכם ליעד...</p>
      </div>
    </div>
  );
}
